import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { type DocumentNode, Kind, print } from 'graphql'

import { getBrowserGraphqlEndpoint, getServerGraphqlEndpoint } from './graphql-endpoint'

type TrackingEvent = Record<string, unknown>
type TrackingWindow = Window & { dataLayer?: TrackingEvent[] }
type TrackingEventTransformer = (events: TrackingEvent[]) => Promise<TrackingEvent[]>

let beforeExecuteListener: ((query: string) => Promise<void>) | null = null
let trackingEventTransformer: TrackingEventTransformer | null = null

export const registerBeforeExecuteListener = (
  listener: ((query: string) => Promise<void>) | null
) => {
  beforeExecuteListener = listener
}

const notifyBeforeExecute = async (query: string) => {
  if (beforeExecuteListener) await beforeExecuteListener(query)
}

export const registerTrackingEventTransformer = (transformer: TrackingEventTransformer | null) => {
  trackingEventTransformer = transformer
}

const transformTrackingEvents = async (events: TrackingEvent[]) => {
  if (!trackingEventTransformer) return events

  try {
    return await trackingEventTransformer(events)
  } catch {
    return events
  }
}

/** @public part of the GraphQLExecuteError contract for callers catching by type */
export type GraphQLResponseError = {
  message: string
  path?: ReadonlyArray<string | number>
  extensions?: Record<string, unknown>
}

/** @public thrown by every executor — exported so callers can narrow via instanceof */
export class GraphQLExecuteError extends Error {
  readonly operationName: string
  readonly status: number
  /** Full errors array from the response — `message` alone would drop extensions/code. */
  readonly errors: GraphQLResponseError[]

  constructor(
    operationName: string,
    status: number,
    errors: GraphQLResponseError[],
    fallbackMessage?: string
  ) {
    super(errors[0]?.message || fallbackMessage || 'Error fetching data!')
    this.name = 'GraphQLExecuteError'
    this.operationName = operationName
    this.status = status
    this.errors = errors
  }
}

export const getGraphQLErrorCode = (error: unknown): string | undefined => {
  if (!(error instanceof GraphQLExecuteError)) return undefined

  const code = error.errors[0]?.extensions?.code
  return typeof code === 'string' ? code : undefined
}

export type ExecuteOptions = {
  headers?: HeadersInit
  signal?: AbortSignal
  /** Passed through to fetch for Next.js data-cache control on the server. */
  next?: NextFetchRequestConfig
  cache?: RequestCache
}

export const getOperationName = (document: DocumentNode): string => {
  const operation = document.definitions.find(
    definition => definition.kind === Kind.OPERATION_DEFINITION
  )
  return (operation && 'name' in operation && operation.name?.value) || 'unknown'
}

/**
 * Variables tuple for graphql() document calls: the variables argument is
 * omittable exactly when the operation has none. TRest appends the caller's
 * trailing options parameter, so every executor/hook shares this one shape.
 */
export type VariablesArg<TVariables, TRest extends unknown[] = [options?: ExecuteOptions]> =
  TVariables extends Record<string, never>
    ? [variables?: undefined, ...TRest]
    : [variables: TVariables, ...TRest]

/**
 * Internal executor with a plain (non-tuple) signature. Deliberately loose:
 * `variables` is optional even for operations that require them, because TS
 * cannot verify assignment into the conditional VariablesArg tuple inside a
 * generic caller. Public callers should use execute() / executeBackend() /
 * the hooks, which keep the strict signature.
 */
export async function executeOperation<TResult, TVariables>(
  document: TypedDocumentNode<TResult, TVariables>,
  variables: TVariables | undefined,
  options?: ExecuteOptions
): Promise<TResult> {
  const query = print(document)
  const operationName = getOperationName(document)
  await notifyBeforeExecute(query)

  const isServer = typeof window === 'undefined'
  const endpoint = isServer ? getServerGraphqlEndpoint() : getBrowserGraphqlEndpoint()
  const headers = new Headers(options?.headers)

  headers.set('Content-Type', 'application/json')
  if (!headers.has('X-Client-Identifier')) {
    headers.set('X-Client-Identifier', isServer ? '7f-frontend-server' : '7f-frontend-client')
  }

  const res = await fetch(endpoint, {
    method: 'POST',
    credentials: 'include',
    headers,
    signal: options?.signal,
    next: options?.next,
    cache: options?.cache,
    body: JSON.stringify({
      operationName,
      query,
      variables,
    }),
  })

  const trackingEvents = isServer ? null : res.headers.get('X-Dl-Events')
  if (trackingEvents) {
    const trackingWindow = window as TrackingWindow
    trackingWindow.dataLayer ??= []
    const events = await transformTrackingEvents(
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- JSON.parse is untyped
      JSON.parse(window.atob(trackingEvents)) as TrackingEvent[]
    )

    events.forEach(event => {
      trackingWindow.dataLayer?.push(event)
    })
  }

  let json: { data?: TResult; errors?: GraphQLResponseError[] } | null = null
  try {
    json = await res.json()
  } catch {
    // Non-JSON body (proxy/gateway error page) — handled below via status.
    json = null
  }

  if (json?.errors?.length) {
    throw new GraphQLExecuteError(operationName, res.status, json.errors)
  }
  if (!res.ok || json?.data === undefined) {
    throw new GraphQLExecuteError(
      operationName,
      res.status,
      [],
      `GraphQL request "${operationName}" failed with status ${res.status}`
    )
  }
  return json.data
}

export async function execute<TResult, TVariables>(
  document: TypedDocumentNode<TResult, TVariables>,
  ...[variables, options]: VariablesArg<TVariables>
): Promise<TResult> {
  return executeOperation(document, variables, options)
}
